import { createContext, use, useSyncExternalStore } from "react";
import type { JSX, ReactNode } from "react";
import type { SyncStatus } from "./SyncEngine";

/** What the UI needs from the synchronization. */
export interface SyncController {
  /** Starts a synchronization. */
  readonly syncNow: () => Promise<void>;
  /** Returns the current status. */
  readonly getStatus: () => SyncStatus;
  /** Subscribes to status changes. */
  readonly subscribe: (listener: (status: SyncStatus) => void) => () => void;
}

const SyncContext = createContext<SyncController | null>(null);

/** Properties of {@link SyncProvider}. */
export interface SyncProviderProps {
  readonly controller: SyncController;
  readonly children: ReactNode;
}

/**
 * Provides the synchronization controller.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function SyncProvider(props: SyncProviderProps): JSX.Element {
  const provider = <SyncContext value={props.controller}>{props.children}</SyncContext>;

  return provider;
}

/**
 * Returns the synchronization controller.
 *
 * @returns The controller.
 * @throws Error if no {@link SyncProvider} is present.
 */
export function useSync(): SyncController {
  const controller = use(SyncContext);

  if (controller === null) {
    throw new Error("useSync must be used inside a SyncProvider.");
  }

  return controller;
}

/**
 * Returns the synchronization status and re-renders on changes.
 *
 * @returns The status.
 */
export function useSyncStatus(): SyncStatus {
  const controller = useSync();
  const status = useSyncExternalStore(controller.subscribe, controller.getStatus);

  return status;
}
